import devicesModalStyle from "../devices-modal.module.css";

import { useState } from "react";
import { useDispatch } from "react-redux";
import { Row, Col } from "react-bootstrap";

// Service
import AuthDeviceService from "services/Auth.Device.service";
import AuthOrganizationService from "services/Auth.Organization.service";

// Package
import { useTranslation } from "react-i18next";
import { isNil } from "lodash";

// Slice
import { uiActions } from "stores/slice/ui";

// UI components
import Icon from "components/Icon";
import Input from "components/Input";
import Button from "components/Button";
import LinkerWithA from "components/LinkerWithA";
import RadioButton from "components/RadioButton";
import ModalContainer from "components/ModalContainer";
import ButtonWithIcon from "components/ButtonWithIcon";
import InputWithUploadButton from "components/InputWithUploadButton";

const AddDeviceUIDField = ({
  id,
  inputField,
  handleChange,
  handleBlur,
  handleFocus,
  style,
  handleRemoveField,
}) => {
  const { t } = useTranslation();
  return (
    <Col sm={6}>
      <div className="modal-form-title required">
        {t("e7d22294bd")} #{id}
      </div>
      <div className={devicesModalStyle["add-device-input-group"]}>
        <Input
          type="text"
          value={inputField}
          style={style}
          minLength={0}
          maxLength={14}
          onChange={(e) => {
            handleChange(e.target.value, id - 1);
          }}
          onFocus={() => {
            handleFocus(id - 1);
          }}
          onBlur={() => {
            handleBlur();
          }}
        />
        {id > 1 && (
          <Icon
            iconTitle="delete"
            className={`icon-close ${devicesModalStyle["icon"]}`}
            onClick={() => {
              handleRemoveField(id - 1);
              handleBlur();
            }}
          />
        )}
      </div>
    </Col>
  );
};

const AddDeviceModal = (props) => {
  const { modalStatus, changeModalStatus, addDevice, submitModal, orgId } =
    props;
  const [addDeviceMode, setAddDeviceMode] = useState("add");
  const [showAddDeviceButton, setShowAddDeviceButton] = useState(false);
  const [enableSave, setEnableSave] = useState(false);
  const [inputFields, setInputFields] = useState([""]);
  const [inputValid, setInputValid] = useState([{}]);
  const [file, setFile] = useState(null);
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const resetInputs = () => {
    setInputFields([""]);
    setInputValid([{}]);
    changeModalStatus("addDevice", false);
    setShowAddDeviceButton(false);
    setAddDeviceMode("add");
    setFile(null);
  };

  const handleAddField = () => {
    setInputFields([...inputFields, ""]);
    setInputValid([...inputValid, {}]);
    setShowAddDeviceButton(false);
  };

  const handleRemoveField = (index) => {
    const newInputFields = inputFields.filter((input, i) => i !== index);
    setInputFields(newInputFields);
    setInputValid(inputValid.filter((input, i) => i !== index));
    const checkResult = verifyUID(newInputFields);
    setShowAddDeviceButton(checkResult);
    setEnableSave(checkResult);
  };

  const isUIDValid = (input) => {
    const regex = /^[^-]{4}-[^-]{4}-[^-]{4}$/i;
    if (input.trim().length === 0) return true;
    if (input.trim().length === 12 && !input.trim().includes("-")) return true;
    if (input.trim().length === 14) {
      return regex.test(input);
    }
    return false;
  };

  const verifyUID = (newInputFields) => {
    const InputFields = newInputFields.filter((field) => isUIDValid(field));
    return InputFields.length === newInputFields.length;
  };

  const handleChange = (value, index) => {
    const newInputFields = [...inputFields];
    newInputFields[index] = value;
    setInputFields(newInputFields);
    const checkResult = verifyUID(newInputFields);
    setShowAddDeviceButton(checkResult);
    setEnableSave(checkResult);
  };

  const handleBlur = () => {
    const newInputValid = inputFields.map((field) => {
      return isUIDValid(field) ? {} : { borderColor: "red" };
    });
    setInputValid(newInputValid);
  };

  const handleFocus = (index) => {
    const newInputValid = inputValid.map((field, i) => {
      return index === i ? { borderColor: "#0dcaf0" } : field;
    });
    setInputValid(newInputValid);
  };

  const fileSelect = (target) => {
    setFile(target);
  };

  const submit = async () => {
    try {
      resetInputs();
      dispatch(uiActions.showSpinner());
      const res = await AuthOrganizationService.addDeviceCsv(orgId, file);
      dispatch(uiActions.hideSpinner());
      if (res?.data) {
        submitModal(res?.data.log);
      } else {
        submitModal([{ error: res.error, uid: "" }]);
      }
    } catch (error) {
      console.debug("AddDevice submit:", error);
    } finally {
      dispatch(uiActions.hideSpinner());
    }
  };

  return (
    <div className={devicesModalStyle["inventory-add-device-modal"]}>
      <ModalContainer
        modalWidthType="modal-500px"
        openModal={modalStatus.addDevice.status}
        closeModal={() => {
          resetInputs();
        }}
      >
        <div className="header">
          <div className="title">{t("e1fde754c3")}</div>
        </div>
        <div className="body">
          {/* Add device */}
          <div>
            <div className="modal-form-title">
              <RadioButton
                id="AddDevice"
                name="AddDevice"
                label={t("5290e89436")}
                checked={addDeviceMode === "add"}
                labelClassName="form-title"
                onChange={() => {
                  setAddDeviceMode("add");
                  setFile(null);
                  setInputFields([""]);
                  setInputValid([{}]);
                }}
              />
            </div>
          </div>

          {/* Add device content */}
          <div
            className={`mb-1 ms-3 ${
              addDeviceMode === "add" ? "d-block" : "d-none"
            }`}
          >
            <Row>
              {inputFields.map((inputField, index) => (
                <AddDeviceUIDField
                  key={index}
                  id={index + 1}
                  inputField={inputField}
                  handleChange={handleChange}
                  handleBlur={handleBlur}
                  handleFocus={handleFocus}
                  style={inputValid[index]}
                  handleRemoveField={handleRemoveField}
                />
              ))}
            </Row>
            <div
              className={`mt-2 mb-3 ${devicesModalStyle["add-device-hint"]}`}
            >
              {t("039a262e9b")}
            </div>
            {showAddDeviceButton && (
              <ButtonWithIcon
                label="Add"
                className="d-flex justify-content-center"
                iconClassName="icon-expand"
                onClick={handleAddField}
              ></ButtonWithIcon>
            )}
          </div>

          {/* Bulk import */}
          <div className="mt-3">
            <div className="modal-form-title">
              <RadioButton
                id="bulkImport"
                name="bulkImport"
                label={t("318c3955e6")}
                checked={addDeviceMode === "bulk"}
                labelClassName="form-title"
                onChange={() => {
                  setAddDeviceMode("bulk");
                  setEnableSave(false);
                }}
              />
            </div>
          </div>

          {/* Bulk import content */}
          <div
            className={`mb-1 ms-3 ${
              addDeviceMode === "bulk" ? "d-block" : "d-none"
            }`}
          >
            <div className={`mb-1 ${devicesModalStyle["bulk-import-hint"]}`}>
              {t("895378c44a")}
            </div>
            <InputWithUploadButton
              containerClassName="my-2"
              accept="text/csv"
              value={file?.name || ""}
              onClick={(e) => {
                fileSelect(null);
              }}
              onChange={(e) => {
                fileSelect(e.target.files[0]);
              }}
            />
            <div className="fst-italic">
              <span>{t("602ec1add4")}</span>
              <LinkerWithA
                label={t("bc98d84673")}
                href="/file/sample_bulk_inventory_creator.csv"
                className="linker-blue text-decoration-none mx-1 fw-light"
                onClick={() => {}}
              />
            </div>
          </div>
        </div>
        <div className={`footer ${devicesModalStyle["footer"]}`}>
          <Button
            label="Cancel"
            className="btn-cancel"
            onClick={() => {
              resetInputs();
            }}
          />
          <Button
            label="Save"
            disabled={!enableSave && isNil(file)}
            className="btn-submit"
            onClick={() => {
              if (addDeviceMode === "add") {
                setEnableSave(false);
                addDevice(inputFields);
                resetInputs();
                setShowAddDeviceButton(false);
              } else {
                submit();
              }
            }}
          />
        </div>
      </ModalContainer>
    </div>
  );
};

export default AddDeviceModal;
